import React, { useState } from 'react';
import { Modal, Select } from '../../../design-system';
import { useAdminStore } from '../../../store/adminStore';
import styles from './RejectDialog.module.css';

interface RejectDialogProps {
  onReject: (reason: string, notes: string) => void;
}

const rejectionReasons = [
  { value: 'incomplete_docs', label: 'Incomplete documentation' },
  { value: 'invalid_tax_id', label: 'Invalid tax ID' },
  { value: 'suspicious_activity', label: 'Suspected fraudulent activity' },
  { value: 'policy_violation', label: 'Business does not meet our criteria' },
  { value: 'other', label: 'Other reason' },
];

export const RejectDialog: React.FC<RejectDialogProps> = ({ onReject }) => {
  const {
    rejectDialogOpen,
    rejectionReason,
    rejectionNotes,
    setRejectionReason,
    setRejectionNotes,
    closeRejectDialog,
    isLoading,
  } = useAdminStore();

  const [localReason, setLocalReason] = useState(rejectionReason);
  const [localNotes, setLocalNotes] = useState(rejectionNotes);
  const [error, setError] = useState<string | null>(null);

  const handleReject = async () => {
    if (!localReason) {
      setError('Please select a reason for rejection');
      return;
    }

    setRejectionReason(localReason);
    setRejectionNotes(localNotes);
    onReject(localReason, localNotes);
  };

  return (
    <Modal
      isOpen={rejectDialogOpen}
      onClose={closeRejectDialog}
      size="md"
      closeOnEsc={!isLoading}
      closeOnBackdropClick={!isLoading}
    >
      <Modal.Header>
        <h2 className={styles.title}>Reject Application?</h2>
      </Modal.Header>

      <Modal.Body>
        <div className={styles.content}>
          <p className={styles.message}>
            This action will:
          </p>
          <ul className={styles.actionList}>
            <li>Reject the application</li>
            <li>Send a rejection email to the applicant</li>
            <li>Include the reason and any notes you provide</li>
          </ul>

          <div className={styles.formGroup}>
            <label htmlFor="rejection-reason" className={styles.label}>
              Reason for Rejection *
            </label>
            <Select
              id="rejection-reason"
              value={localReason}
              onChange={(e) => {
                setLocalReason(e.target.value);
                setError(null);
              }}
              options={[
                { value: '', label: 'Select a reason...' },
                ...rejectionReasons,
              ]}
              disabled={isLoading}
            />
            {error && <p className={styles.error}>{error}</p>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="rejection-notes" className={styles.label}>
              Explanation for Applicant
            </label>
            <textarea
              id="rejection-notes"
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              placeholder="Explain why the application was rejected. This will be sent to the applicant in the rejection email."
              className={styles.textarea}
              rows={4}
              disabled={isLoading}
            />
            <p className={styles.helperText}>
              Be professional and constructive. The applicant will receive this in the rejection email.
            </p>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={closeRejectDialog}
            disabled={isLoading}
            type="button"
          >
            Cancel
          </button>
          <button
            className={styles.confirmButton}
            onClick={handleReject}
            disabled={isLoading || !localReason}
            type="button"
          >
            {isLoading ? 'Rejecting...' : '✕ Reject Application'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
